import { onBeforeUnmount, ref, unref, watch } from 'vue';
import { searchResidents } from '../../api/qr/residentSearch';
import { isOutsideQrIntercomGeofence } from '../../api/qr/qrIntercom';
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from '../../constants/qr';

// No leading whitespace and at most one space between words
export const normalizeSearchQuery = value => String(value ?? '').replace(/\s+/g, ' ').replace(/^ /, '');

export function useResidentSearch(options = {}) {
    const query = ref(normalizeSearchQuery(options.initialQuery));
    const results = ref([]);
    const loading = ref(false);
    const error = ref(false);
    const last = ref(true);
    const hasSearched = ref(false);

    let page = 0;
    let failedPage = 0;
    let requestSeq = 0;
    let debounceTimer = null;

    const currentTerm = () => query.value.trim();

    async function fetchPage(term, pageToLoad) {
        const seq = ++requestSeq;
        loading.value = true;
        error.value = false;
        try {
            const request = {
                query: term,
                page: pageToLoad,
                intercomId: unref(options.intercomId),
            };
            const coordinates = unref(options.coordinates);
            if (coordinates) request.coordinates = coordinates;
            const response = await searchResidents(request);
            if (seq !== requestSeq) return;
            results.value = pageToLoad === 0 ? response.items : [...results.value, ...response.items];
            last.value = response.last;
            page = pageToLoad;
            hasSearched.value = true;
        } catch (requestError) {
            if (seq !== requestSeq) return;
            if (isOutsideQrIntercomGeofence(requestError)) {
                options.onOutsideGeofence?.(requestError);
                return;
            }
            if (pageToLoad === 0) results.value = [];
            failedPage = pageToLoad;
            error.value = true;
        } finally {
            if (seq === requestSeq) loading.value = false;
        }
    }

    function reset() {
        requestSeq++;
        results.value = [];
        loading.value = false;
        error.value = false;
        last.value = true;
        hasSearched.value = false;
        page = 0;
    }

    function retry() {
        const term = currentTerm();
        if (term.length < MIN_SEARCH_LENGTH || loading.value) return;
        return fetchPage(term, failedPage);
    }

    function loadMore() {
        const term = currentTerm();
        if (loading.value || error.value || last.value || term.length < MIN_SEARCH_LENGTH) return;
        return fetchPage(term, page + 1);
    }

    watch(() => currentTerm(), term => {
        clearTimeout(debounceTimer);
        if (term.length < MIN_SEARCH_LENGTH) {
            reset();
            return;
        }
        // Keep previous results visible while the next query is debounced
        requestSeq++;
        loading.value = true;
        error.value = false;
        debounceTimer = setTimeout(() => fetchPage(term, 0), SEARCH_DEBOUNCE_MS);
    });

    // A restored query (e.g. after a call) loads immediately instead of waiting for input
    if (currentTerm().length >= MIN_SEARCH_LENGTH) fetchPage(currentTerm(), 0);

    onBeforeUnmount(() => {
        clearTimeout(debounceTimer);
        requestSeq++;
    });

    return { query, results, loading, error, last, hasSearched, loadMore, retry };
}
